// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.
//
// D-POT-7 — "Start a POT" by hand (staff): pick any patient, one of their open therapies and the visit
// date; the visit lands in the Queue to be claimed. The ready-to-start rows in the Queue cover the
// enrolled + consented patients; this is for everyone else (D-CRX-26 S3's manual start, on the kit).
import { useState } from 'react'
import Box from '@mui/material/Box'
import AppButton from '../../components/ui/AppButton'
import AppDialog from '../../components/ui/AppDialog'
import AutocompleteField from '../../components/ui/AutocompleteField'
import DateInput from '../../components/ui/DateInput'
import { SelectField } from '../../components/ui/FormFields'
import { useOpenTherapies } from '../../hooks/queries/carerxClinical'
import { usePotPatientSearch, useStartPot } from '../../hooks/queries/carerxPot'
import { apiErrorMessage } from './carerxPotRequest'

export default function CareRxPotManualStartDialog({ open, onClose, onStarted }: { open: boolean; onClose: () => void; onStarted: () => void }) {
  const [q, setQ] = useState('')
  const [patient, setPatient] = useState<{ id: string; name: string } | null>(null)
  const [therapy, setTherapy] = useState('')
  const [date, setDate] = useState('')
  const [err, setErr] = useState<string | null>(null)
  const search = usePotPatientSearch(patient ? '' : q)
  const therapies = useOpenTherapies(patient?.id ?? null)
  const start = useStartPot()

  const reset = () => { setQ(''); setPatient(null); setTherapy(''); setDate(''); setErr(null) }
  const close = () => { reset(); onClose() }
  const submit = async () => {
    if (!therapy || !date) return
    setErr(null)
    try { await start.mutateAsync({ therapyId: therapy, visitDate: date }); reset(); onStarted() }
    catch (e) { setErr(apiErrorMessage(e, 'Could not start the POT')) }
  }
  const options = (search.data ?? []).map(p => ({ value: p.id, label: p.display_name, secondary: p.mrn ? `MRN ${p.mrn}` : undefined }))
  const open_ = therapies.data ?? []

  return (
    <AppDialog open={open} onClose={close} title="Start a POT" maxWidth="sm"
      actions={<>
        <AppButton onClick={close}>Cancel</AppButton>
        <AppButton variant="contained" disabled={!therapy || !date || start.isPending} onClick={() => { void submit() }}>{start.isPending ? 'Starting…' : 'Start POT'}</AppButton>
      </>}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 0.5 }}>
        <AutocompleteField ariaLabel="Patient" placeholder="Find patient (name or MRN)…" value={q} loading={search.isFetching}
          onInputChange={v => { setQ(v); if (patient) { setPatient(null); setTherapy('') } }}
          options={options} onPick={o => { setPatient({ id: o.value, name: o.label }); setQ(o.label) }} />
        {patient && (therapies.isLoading ? <Box sx={{ fontSize: 13 }}>Loading therapies…</Box>
          : open_.length === 0
            ? <Box sx={{ fontSize: 13, color: 'text.secondary' }}>No open therapies — enroll from the patient profile first (the enrollment creates the therapy the POT hangs on).</Box>
            : <SelectField size="small" label="Therapy" value={therapy} onChange={setTherapy}
                options={open_.map(t => ({ value: t.id, label: t.drug_name ?? 'Therapy' }))} />)}
        <DateInput label="Visit date" value={date} onChange={v => setDate(v ?? '')} />
        {err && <Box role="alert" sx={{ fontSize: 13, color: 'error.main' }}>{err}</Box>}
      </Box>
    </AppDialog>
  )
}
